import {
  ChevronDown,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Search,
} from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  ScopeActivationItem,
  ScopeActivationResponse,
} from "@/api/scope-activation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatCount } from "@/lib/format";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  distinctLobs,
  filterScopeItems,
  scopeItemsToCsv,
  scopeItemsToMatrix,
} from "./scopeActivationFilter";
import { confidenceLabel } from "./aderencia/aderenciaData";

const VERDICT_META: Record<string, { label: string; cls: string }> = {
  activate: { label: "Activate", cls: "bg-emerald-100 text-emerald-700" },
  needs_decision: { label: "Decision", cls: "bg-amber-100 text-amber-700" },
  needs_review: { label: "Review", cls: "bg-orange-100 text-orange-700" },
  skip: { label: "Not applicable", cls: "bg-gray-100 text-gray-500" },
};

const ALL = "__all__";

// Tiles de resumo, clicáveis: filtram por veredito (toggle).
const TILES: Array<{ key: string; label: string; sumKey: keyof ScopeActivationResponse["summary"] }> = [
  { key: ALL, label: "Total", sumKey: "total" },
  { key: "activate", label: "Activate", sumKey: "activate" },
  { key: "needs_decision", label: "Decision", sumKey: "needs_decision" },
  { key: "needs_review", label: "Review", sumKey: "needs_review" },
  { key: "skip", label: "Not applicable", sumKey: "skip" },
];

function downloadCsv(filename: string, content: string) {
  // BOM UTF-8 pro Excel reconhecer acentos
  const url = URL.createObjectURL(
    new Blob(["﻿" + content], { type: "text/csv;charset=utf-8;" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function downloadXlsx(filename: string, items: ScopeActivationItem[]) {
  // import dinâmico: a lib só carrega quando o usuário exporta
  const XLSX = await import("xlsx");
  const ws = XLSX.utils.aoa_to_sheet(scopeItemsToMatrix(items));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Fit-to-Standard");
  XLSX.writeFile(wb, filename);
}

export function ScopeActivationResult({ data }: { data: ScopeActivationResponse }) {
  const { t } = useTranslation();
  const [verdict, setVerdict] = useState<string>("activate");
  const [lob, setLob] = useState<string>(ALL);
  const [search, setSearch] = useState<string>("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const lobs = useMemo(() => distinctLobs(data.items), [data.items]);
  const filtered = useMemo(
    () =>
      filterScopeItems(data.items, {
        verdict: verdict === ALL ? "" : verdict,
        lob: lob === ALL ? "" : lob,
        search,
      }),
    [data.items, verdict, lob, search],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* tiles de resumo, clicáveis */}
      <div className="grid grid-cols-5 gap-2">
        {TILES.map((tile) => {
          const active = verdict === tile.key;
          return (
            <button
              key={tile.key}
              onClick={() => setVerdict(tile.key)}
              className={`rounded-md border p-2 text-left transition ${
                active ? "border-emerald-500 bg-emerald-50" : "border-gray-200 bg-white hover:bg-gray-50"
              }`}
            >
              <div className="text-[10px] uppercase text-muted-foreground">{t(tile.label)}</div>
              <div className="text-lg font-semibold">{data.summary[tile.sumKey]}</div>
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <span>
          {t("Clean Core")}: <b>{formatCount(data.summary.clean_core)}</b>
        </span>
        <span>·</span>
        <span>
          {t("Review (suggested)")}: <b>{formatCount(data.summary.suggested_review)}</b>
        </span>
        <span>·</span>
        <span>
          {t("{{count}} tables probed · rules v{{version}}", {
            count: formatCount(data.fingerprint_tables),
            version: data.version,
          })}
        </span>
      </div>

      {/* barra de filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={lob} onValueChange={setLob}>
          <SelectTrigger className="h-8 w-56 text-xs">
            <SelectValue placeholder={t("LoB")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL} className="text-xs">
              {t("All LoBs")}
            </SelectItem>
            {lobs.map((l) => (
              <SelectItem key={l} value={l} className="text-xs">{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Search scope item...")}
            className="h-8 w-48 pl-7 text-xs"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground">
            {t("{{shown}} of {{total}}", {
              shown: formatCount(filtered.length),
              total: formatCount(data.items.length),
            })}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            onClick={() =>
              downloadCsv("aderencia-scope-items.csv", scopeItemsToCsv(filtered))
            }
          >
            <Download className="h-3.5 w-3.5" /> {t("CSV")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            onClick={() => downloadXlsx("aderencia-scope-items.xlsx", filtered)}
          >
            <FileSpreadsheet className="h-3.5 w-3.5" /> {t("Excel")}
          </Button>
        </div>
      </div>

      {/* tabela master-detail: linha = ação (ativar item); expande = porquês */}
      <div className="rounded-md border">
        <Table>
          <TableHeader className="bg-white">
            <TableRow>
              <TableHead className="w-6 text-xs"></TableHead>
              <TableHead className="text-xs">{t("Public action")}</TableHead>
              <TableHead className="text-xs">{t("LoB")}</TableHead>
              <TableHead className="text-xs">{t("Verdict")}</TableHead>
              <TableHead className="text-xs">{t("Confidence")}</TableHead>
              <TableHead className="text-xs">Clean Core</TableHead>
              <TableHead className="text-xs text-right">{t("Capabilities")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((i) => {
              const m = VERDICT_META[i.verdict] ?? { label: i.verdict, cls: "" };
              const open = expanded.has(i.scope_item_id);
              const caps = i.capability_details;
              return (
                <Fragment key={i.scope_item_id}>
                  <TableRow
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => toggle(i.scope_item_id)}
                  >
                    <TableCell className="py-1.5">
                      {caps.length > 0 ? (
                        open ? (
                          <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5" />
                        )
                      ) : null}
                    </TableCell>
                    <TableCell className="text-xs">
                      <span className="font-mono font-semibold">{i.scope_item_id}</span>
                      <span className="text-muted-foreground"> — {i.scope_item_name || "-"}</span>
                    </TableCell>
                    <TableCell className="text-xs">{i.lob || "-"}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={`text-[10px] ${m.cls}`}>{t(m.label)}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">{t(confidenceLabel(i.confidence))}</TableCell>
                    <TableCell className="text-xs">{i.clean_core ? `⚠ ${t("yes")}` : ""}</TableCell>
                    <TableCell className="text-xs text-right text-muted-foreground">
                      {caps.length}
                    </TableCell>
                  </TableRow>
                  {open && caps.length > 0 && (
                    <TableRow className="bg-gray-50/60 hover:bg-gray-50/60">
                      <TableCell />
                      <TableCell colSpan={6} className="py-2">
                        <div className="text-[10px] uppercase text-muted-foreground mb-1">
                          {t("Why activate — capabilities detected in ERP")}
                        </div>
                        <div className="flex flex-col gap-1.5">
                          {caps.map((c, idx) => (
                            <div
                              key={idx}
                              className="grid grid-cols-[1.4fr_1fr_1fr] gap-2 text-xs border-l-2 border-emerald-300 pl-2"
                            >
                              <span>{c.capability || "-"}</span>
                              <span className="text-muted-foreground">
                                {t("signal: {{signal}}", { signal: c.triggered_by.join(", ") || "-" })}
                              </span>
                              <span>→ {c.s4_target || "-"}</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-xs text-muted-foreground py-6">
                  {t("No items for the selected filters.")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
